import { useState, useEffect, useCallback } from 'react';
import {
  fetchProducerMembers,
  addProducerMember,
  removeProducerMember,
  ProducerMember,
  ProducerMemberRole,
} from '@/data/producers/members';

export function useProducerMembers(producerId: string | undefined) {
  const [members, setMembers] = useState<ProducerMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadMembers = useCallback(async () => {
    if (!producerId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchProducerMembers(producerId);
      setMembers(data);
    } catch (err: any) {
      console.error('Failed to load members:', err);
      setError(err.message || 'Kunde inte hämta teammedlemmar');
    } finally {
      setIsLoading(false);
    }
  }, [producerId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const addMember = async (email: string, role: ProducerMemberRole) => {
    if (!producerId) throw new Error('Producer ID is required');

    const newMember = await addProducerMember(producerId, email, role);
    setMembers((prev) => [...prev, newMember]);
    return newMember;
  };

  const removeMember = async (userId: string) => {
    if (!producerId) throw new Error('Producer ID is required');

    await removeProducerMember(producerId, userId);
    setMembers((prev) => prev.filter((m) => m.user_id !== userId));
  };

  return {
    members,
    isLoading,
    error,
    addMember,
    removeMember,
    refresh: loadMembers,
  };
}
